import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Form, useNavigation } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';
import { TemporalFormatter } from '@/components/TemporalFormatter';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { LeadNote } from '@/models/entities/lead';
import Actions from '@/models/ui/actions.ts';

export interface LeadNotesProps {
  readonly maxLength?: number;
  readonly notesHistory: LeadNote[];
  readonly readonly?: boolean;
}

function getInitials(createdBy?: LeadNote['createdBy']) {
  if (!createdBy) return 'SU';
  if (createdBy.firstName && createdBy.lastName) {
    return `${createdBy.firstName[0]}${createdBy.lastName[0]}`.toUpperCase();
  }
  if (createdBy.firstName) return createdBy.firstName[0].toUpperCase();
  return createdBy.email[0].toUpperCase();
}

function getCreatorName(createdBy?: LeadNote['createdBy']) {
  if (!createdBy) return 'System User';
  if (createdBy.firstName && createdBy.lastName) {
    return `${createdBy.firstName} ${createdBy.lastName}`;
  }
  if (createdBy.firstName) return createdBy.firstName;
  return createdBy.email;
}

export function LeadNotes({
  maxLength = 1024,
  notesHistory = [],
  readonly = false,
}: LeadNotesProps) {
  const [notes, setNotes] = useState<string>('');
  const navigation = useNavigation();

  // Reset notes textarea after successful POST (revalidation completes and goes to idle)
  useEffect(() => {
    if (navigation.state === 'idle') {
      setNotes('');
    }
  }, [navigation.state]);

  const handleCancel = () => {
    setNotes('');
  };

  // Sort descending by createdAt date
  const sortedNotes = [...notesHistory].sort((a, b) =>
    Temporal.Instant.compare(b.createdAt, a.createdAt),
  );

  return (
    <section aria-labelledby="notes-title" className="flex flex-col gap-4">
      <div>
        <h3
          id="notes-title"
          className="inline-block bg-[#e8e8e8] px-2 py-1 text-xs font-semibold rounded mb-2"
        >
          NOTES
        </h3>
        <div className="border-b border-gray-300" aria-hidden="true"></div>
      </div>

      {/* Notes Timeline Stream */}
      <div className="flex flex-col gap-3 max-h-60 overflow-y-auto pr-1">
        {sortedNotes.length === 0 ? (
          <p className="text-xs text-gray-500 italic">
            No historical notes added yet.
          </p>
        ) : (
          sortedNotes.map((note) => (
            <div
              key={note.id}
              className="group bg-white hover:bg-slate-50/20 border border-slate-100 border-l-3 border-l-indigo-500 p-3 rounded-r-lg flex flex-col gap-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] hover:shadow-sm transition-all duration-200"
            >
              {/* Card Header: Creator Info & Date */}
              <div className="flex items-center justify-between gap-2 border-b border-slate-100/60 pb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-indigo-50/80 border border-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-600 uppercase select-none shrink-0">
                    {getInitials(note.createdBy)}
                  </div>
                  <div className="flex flex-col">
                    {note.createdBy?.email ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-[11px] font-semibold text-slate-700 leading-tight cursor-help border-b border-dotted border-slate-300 hover:text-indigo-600 hover:border-indigo-400 transition-colors">
                              {getCreatorName(note.createdBy)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="text-[10px] bg-slate-900 text-white border border-slate-800 rounded px-2 py-1 shadow-md">
                            {note.createdBy.email}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-[11px] font-semibold text-slate-700 leading-tight">
                        {getCreatorName(note.createdBy)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-[9px] text-slate-400 font-medium flex items-center gap-1 shrink-0 self-start">
                  <Clock className="w-3 h-3 text-slate-300" />
                  <TemporalFormatter value={note.createdAt} />
                </div>
              </div>

              {/* Note Content */}
              <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">
                {note.content}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add Note Section */}
      <Form method="post" className="border-t border-gray-200 pt-4 mt-2">
        <label
          htmlFor="lead-notes-textarea"
          className="block text-xs font-semibold mb-2"
        >
          Add Notes and Deviations
        </label>
        <Textarea
          id="lead-notes-textarea"
          name="note"
          placeholder="Enter text..."
          disabled={readonly}
          className="min-h-24 text-xs resize-none mb-2 bg-white"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={maxLength}
          aria-describedby="notes-char-count"
        />
        <div className="flex items-center justify-between">
          <span
            id="notes-char-count"
            className="text-xs text-gray-500"
            aria-live="polite"
            aria-atomic="true"
          >
            {notes.length}/{maxLength}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={handleCancel}
              disabled={readonly || !notes.trim()}
            >
              CANCEL
            </Button>
            <Button
              type="submit"
              name="action"
              value={Actions.UpdateNote.key}
              size="sm"
              className="bg-[#2d2d2d] hover:bg-[#1d1d1d] text-white text-xs font-semibold"
              disabled={readonly || !notes.trim()}
            >
              {Actions.UpdateNote.label}
            </Button>
          </div>
        </div>
      </Form>
    </section>
  );
}
